import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, HelpCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { useServiceTimes } from '../hooks/useServiceTimes';
import { useSiteContent } from '../hooks/useSiteContent';
import { usePageSettings } from '../hooks/usePageSettings';
import LiveStreamBanner from '../components/LiveStreamBanner';
import LatestSermonsStrip from '../components/LatestSermonsStrip';
import InstagramStrip from '../components/InstagramStrip';
import UpcomingEvents from '../components/UpcomingEvents';
import Footer from '../components/Footer';
import { ChurchJsonLd } from '../components/ChurchJsonLd';
import { HomeNextSteps } from '../components/HomeNextSteps';
import { trackCtaClick } from '../lib/ctaTracking';

const API = process.env.REACT_APP_BACKEND_URL;

const HOME_DEFAULTS = {
  hero_image_url: 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=1600&q=80&fit=crop',
  hero_eyebrow: '',
  hero_heading: '',
  hero_subheading: '',
  sections: [
    { id: 'service-times', label: 'Service Times', enabled: true },
    { id: 'ministries', label: 'Find Your Place / Ministries', enabled: true },
    { id: 'upcoming-events', label: 'Upcoming Events', enabled: true },
    { id: 'latest-sermons', label: 'Latest Sermons', enabled: true },
    { id: 'instagram', label: 'Instagram Feed', enabled: true },
    { id: 'faqs', label: 'FAQs', enabled: true },
    { id: 'cta', label: 'Final CTA', enabled: true },
  ],
};

const Home = () => {
  const services = useServiceTimes();
  const site = useSiteContent();
  const { settings: page } = usePageSettings('home', HOME_DEFAULTS);
  const [faqs, setFaqs] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [hasBanner, setHasBanner] = useState(false);
  const [sundayTime, setSundayTime] = useState('12 PM');

  // Read-only: Sunday time from admin Service Times; keeps "12 PM" if none is set.
  useEffect(() => {
    axios.get(`${API}/api/service-times`).then((res) => {
      const sundays = (res.data.services || []).filter((s) => s.day === 'Sunday' && s.is_active !== false && s.time);
      const sun = sundays.find((s) => /worship/i.test(s.name || '')) || sundays.find((s) => !/school/i.test(s.name || ''));
      if (sun) setSundayTime(sun.time.replace(/:00(?=\s*[AP]M)/i, '').trim());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/site-content/faqs`)
      .then(res => setFaqs(res.data.faqs))
      .catch(() => setFaqs([]));
    axios.get(`${API}/api/ministry-microsites`)
      .then(res => setMinistries(res.data.microsites || []))
      .catch(() => setMinistries([]));
  }, []);

  const heroImage = page.hero_image_url?.startsWith('http')
    ? page.hero_image_url
    : page.hero_image_url
      ? `${API}${page.hero_image_url}`
      : HOME_DEFAULTS.hero_image_url;

  return (
    <div className="min-h-screen bg-white">
      {/* Live / Upcoming banner — fixed below the header, auto-hides when nothing's happening */}
      <div className="fixed top-20 sm:top-24 left-0 right-0 z-40 pointer-events-auto">
        <LiveStreamBanner onVisibilityChange={setHasBanner} />
      </div>

      <ChurchJsonLd site={site} />
      {/* Spacer for the fixed header (and live banner when showing) */}
      <div className={hasBanner ? 'h-36 sm:h-40' : 'h-[65px] sm:h-[81px]'} data-testid="home-header-spacer" />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-white/70"></div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          {/* Location Badge - Only show if address is set */}
          {(site.address_line1 || site.address_line2) && (
            <div className="inline-flex items-center gap-2 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm text-slate-600 mb-6 sm:mb-8 shadow-sm" data-testid="hero-location-badge">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-church-green" />
              {site.address_line1}{site.address_line1 && site.address_line2 && ', '}{site.address_line2}
            </div>
          )}
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
            A Place to Belong<br />
            <span className="text-slate-800">in New Jersey</span>
          </h1>
          
          {/* Tagline */}
          <p className="text-base sm:text-xl md:text-2xl text-slate-700 mb-3 sm:mb-4 font-medium">
            Experience powerful worship, real relationships, and a church where you can grow in your faith and find purpose.
          </p>
          
          <p className="text-sm sm:text-lg text-slate-600 mb-1 sm:mb-2">
            A Multi-Generational, Multicultural, Apostolic Church
          </p>
          
          <p className="text-sm sm:text-base text-slate-500 mb-8 sm:mb-12">
            Where faith becomes real and lives are transformed.
          </p>
          
          {/* Primary CTA */}
          <div className="flex flex-col items-center gap-4">
            <Link to="/plan-your-visit" onClick={() => trackCtaClick('hero-plan-visit')} data-testid="home-hero-plan-visit-btn" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-church-green hover:bg-church-green-dark text-lg sm:text-xl px-8 sm:px-12 py-5 sm:py-7 h-auto font-bold shadow-lg shadow-green-900/20 ring-2 ring-church-green/20 ring-offset-2">
                <Calendar className="w-5 h-5 mr-2" />
                Plan Your First Visit
              </Button>
            </Link>
            <Link to="/watch-live" className="text-church-green font-semibold hover:underline" data-testid="home-hero-watch-online-link">
              Not ready? Watch online first →
            </Link>
            <p className="text-sm text-slate-600 flex flex-wrap items-center justify-center gap-x-2 gap-y-1" data-testid="home-hero-service-info">
              <span className="text-lg sm:text-xl font-bold text-slate-900" data-testid="home-hero-sunday-time">Sundays at {sundayTime}</span>
              <span aria-hidden="true" className="hidden sm:inline">·</span>
              <span>Laurence Harbor, NJ</span>
              <span aria-hidden="true" className="hidden sm:inline">·</span>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=22+Laurence+Parkway%2C+Laurence+Harbor%2C+NJ+08879"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-church-green font-semibold hover:underline"
                data-testid="home-hero-directions-link"
              >
                <MapPin className="w-4 h-4" />Get Directions
              </a>
            </p>
          </div>
        </div>
      </section>

      <HomeNextSteps />

      {/* Dynamic sections — rendered in the order configured via /admin/site-pages */}
      {(page.sections || HOME_DEFAULTS.sections).map((sec) => {
        if (!sec.enabled) return null;
        switch (sec.id) {
          case 'service-times':
            return (
              <section key={sec.id} className="py-20 bg-white" data-testid="section-service-times">
                <div className="container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                      Join Us for Worship
                    </h2>
                    <p className="text-xl text-slate-600 mb-12">
                      All are welcome. Come experience the presence of God.
                    </p>
                    <div className="grid md:grid-cols-3 gap-8">
                      {services.map((s) => (
                        <div key={s.id || `${s.day}-${s.time}`} className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
                          <h3 className="text-2xl font-bold text-church-green mb-2">{s.day}</h3>
                          <p className="text-3xl font-bold text-slate-900 mb-2">{s.time}</p>
                          <p className="text-slate-600">{s.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            );

          case 'ministries':
            return ministries.length > 0 ? (
              <section key={sec.id} className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-white" data-testid="find-your-place-section">
                <div className="container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-12">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                      Find Your Place
                    </h2>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-2">
                      No matter where you are in life, there's a place for you here. Our ministries are designed to help you grow in your faith, build meaningful relationships, and take your next step with God.
                    </p>
                    <p className="text-sm text-church-green font-medium">
                      Explore a ministry below and get connected.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
                    {ministries.map((m) => (
                      <Link
                        key={m.slug}
                        to={`/ministries/${m.slug}`}
                        className="group bg-white rounded-xl border border-slate-200 p-4 sm:p-5 hover:border-church-green hover:shadow-md transition-all duration-200"
                        data-testid={`ministry-card-${m.slug}`}
                      >
                        <p className="text-[11px] font-medium text-church-green mb-0.5">{m.category}</p>
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 group-hover:text-church-green transition-colors">
                          {m.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 mb-3">{m.tagline}</p>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-church-green">
                          Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            ) : null;

          case 'latest-sermons':
            return <LatestSermonsStrip key={sec.id} count={4} />;

          case 'upcoming-events':
            return <UpcomingEvents key={sec.id} />;

          case 'instagram':
            return <InstagramStrip key={sec.id} count={6} />;

          case 'faqs':
            return (
              <section key={sec.id} className="py-20 bg-gradient-to-br from-slate-50 to-white" data-testid="section-faqs">
                <div className="container mx-auto px-4">
                  <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                      <div className="inline-flex items-center gap-2 bg-green-50 px-4 py-2 rounded-full mb-4 border border-church-green/20">
                        <HelpCircle className="w-5 h-5 text-church-green" />
                        <span className="text-sm font-semibold text-church-green">FAQ</span>
                      </div>
                      <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                        Frequently Asked Questions
                      </h2>
                      <p className="text-xl text-slate-600">
                        Everything you need to know before your first visit
                      </p>
                    </div>
                    <div className="space-y-4">
                      {faqs.map((faq) => (
                        <Card key={faq.id || faq.question} className="border-2 hover:border-church-green transition-all duration-300 overflow-hidden">
                          <details className="group">
                            <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                              <h3 className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</h3>
                              <ChevronDown className="w-5 h-5 text-church-green transition-transform group-open:rotate-180 flex-shrink-0" />
                            </summary>
                            <div className="px-6 pb-6 pt-0">
                              <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                            </div>
                          </details>
                        </Card>
                      ))}
                    </div>
                    <div className="text-center mt-8">
                      <Link to="/what-to-expect">
                        <Button size="lg" variant="outline" className="border-2 border-church-green text-church-green hover:bg-church-green hover:text-white">
                          Learn More About Visiting
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            );

          case 'cta':
            return (
              <section key={sec.id} className="py-16 bg-church-green text-white" data-testid="section-cta">
                <div className="container mx-auto px-4 text-center">
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to take your next step?</h2>
                  <p className="text-lg sm:text-xl text-green-50 mb-8 max-w-xl mx-auto" data-testid="home-visit-invitation-text">
                    We’d love to meet you this Sunday.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/plan-your-visit" data-testid="home-cta-plan-visit-btn">
                      <Button size="lg" className="bg-white text-church-green hover:bg-slate-100 w-full sm:w-auto">
                        Plan Your Visit
                      </Button>
                    </Link>
                    <Link to="/bible-study">
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-church-green w-full sm:w-auto">
                        Request a Bible Study
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>
            );

          default:
            return null;
        }
      })}

      {!(page.sections || HOME_DEFAULTS.sections).some((x) => x.id === 'cta' && x.enabled) && (
      <section className="py-14 bg-green-50 border-t border-church-green/10" data-testid="home-visit-invitation">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-5 text-center sm:text-left">
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">We’d love to meet you this Sunday.</p>
          <Link to="/plan-your-visit" data-testid="home-bottom-plan-visit-btn">
            <Button size="lg" className="bg-church-green hover:bg-church-green-dark text-base px-8 py-5 h-auto font-semibold">
              <Calendar className="w-5 h-5 mr-2" />
              Plan Your Visit
            </Button>
          </Link>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;
